import { validate } from '@angular/forms/signals';
import type { ObjectSchemaPathTree } from '../object-schema-path-tree.type';

/**
 * Schema fragment that ensures `password` and `passwordConfirmation` match.
 *
 * Applies a {@link validate} rule to the `passwordConfirmation` field which
 * reactively reads the `password` field. When the values diverge a
 * `{ kind: 'mismatch' }` error is reported on the confirmation field so
 * `idt-password-input` can render the contextual message under the right
 * control. While the confirmation field is empty no error is reported, which
 * matches the existing UX (the `required` validator covers the empty case).
 *
 * @param p An {@link ObjectSchemaPathTree} containing `password` and `passwordConfirmation` fields.
 * @example
 * form(model, (p) => {
 *   required(p.password);
 *   minLength(p.password, 6);
 *   required(p.passwordConfirmation);
 *   passwordMatchValidator(p);
 * });
 */
export function passwordMatchValidator<
  T extends { password: string; passwordConfirmation: string },
>(p: ObjectSchemaPathTree<T>): void {
  validate(p.passwordConfirmation, (ctx) => {
    const password = ctx.valueOf(p.password);
    const confirmation = ctx.value();
    if (confirmation.length === 0 || password === confirmation) {
      return null;
    }
    return { kind: 'mismatch' };
  });
}
